import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import orderBy from "lodash/orderBy";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CountryFlag } from "@dialogueconsulting/sked-ui";
import {
  Popover,
  PopoverAnchor,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "components/ui/command";
import { TooltipWrapper } from "ui";
import { ActionButton } from "ui/SkedCaption/v2/components/action-button";
import { useBrandKit } from "features/brand-kits/hooks/use-brand-kit";
import {
  generateCaptionSuggestions,
  type CaptionSuggestion,
} from "features/brand-kits/api/brand-kits";
import type { Label } from "../../../../entities/posts/components/templates/post-form-base-template";
import { getDescriptions } from "selectors/postFormSelectors";
import { getErrorMessage } from "utils/get-error-message";
import { useTrackEvent } from "libs/analytics";
import { toNative } from "libs/emoji";
import type { ReduxRootState } from "../../../../types/redux-store";
import type { AccountPlatformType } from "../../../../shared/types/accounts";
import {
  ChatGPTSupportedLanguagesData,
  getCountryCodeForLanguage,
} from "shared/language/languages";
import { getRandomCaptionPlaceholder } from "../../../AICaption/ai-caption-placeholders";

type ToneProfile = NonNullable<
  NonNullable<ReturnType<typeof useBrandKit>["brandKit"]>["toneOfVoiceProfiles"]
>[number];

type AiPanel = "generate" | "refine" | "translate" | "settings";
type InlineAiSource = "caption" | "first_comment";

interface Props {
  disabled?: boolean;
  currentCaption: string | null;
  source: InlineAiSource;
  groupId?: string;
  labelIds?: string[];
  selectedContentLabels?: Label[];
  platforms: AccountPlatformType[];
  formId?: string;
  onUndo: () => void;
  onApply: (caption: string, options?: { history: "push" | "merge" }) => void;
}

const REQUESTED_SUGGESTION_COUNT = 1;
const GENERATING_CAPTION_TEXT = "✣ Generating your caption...";
const REGENERATING_CAPTION_TEXT =
  "✣ Regenerating your caption with the same settings...";
const REFINING_CAPTION_TEXT = "✣ Refining your caption...";
const TRANSLATING_CAPTION_TEXT = "✣ Translating your caption...";
const INLINE_ACTION_TOOLBAR_BORDER_WIDTH = 2;
const DEFAULT_TONE_PROFILE_VALUE = "default";
const INLINE_AI_EVENTS = {
  generate: "inline-ai-generate",
  refine: "inline-ai-refine",
  startFresh: "inline-ai-start-fresh",
  toneChanged: "inline-ai-tone-changed",
  translated: "inline-ai-translated",
  tryAgain: "inline-ai-try-again",
} as const;
const loadingCaptionTexts = new Set([
  GENERATING_CAPTION_TEXT,
  REGENERATING_CAPTION_TEXT,
  REFINING_CAPTION_TEXT,
  TRANSLATING_CAPTION_TEXT,
]);

const scrollClass =
  "scrollbar-thin scrollbar-thumb-[#e6e6e6] scrollbar-track-transparent";

const refineOptions = [
  { label: "Longer", instruction: "Make this caption longer." },
  { label: "Shorter", instruction: "Make this caption shorter." },
  {
    label: "Professional",
    instruction: "Rewrite this caption in a professional tone.",
  },
  { label: "Casual", instruction: "Rewrite this caption in a casual tone." },
  {
    label: "Friendly",
    instruction: "Rewrite this caption in a friendly tone.",
  },
  {
    label: "Persuasive",
    instruction: "Rewrite this caption in a persuasive tone.",
  },
];

function getLabelPillColor(label: Label) {
  if (label.isCampaign) {
    return "#edeaff";
  }

  return label.color || "#fffbe9";
}

function getLabelPillIcon(label: Label) {
  if (label.isCampaign) {
    return <i className="fa-solid fa-asterisk" aria-hidden="true" />;
  }

  return label.emoji ? toNative(label.emoji) : "🌼";
}

function getSuggestionCaption(suggestions: CaptionSuggestion[]) {
  return suggestions[0]?.caption?.trim() || "";
}

export function InlineAiCaptionPopover({
  disabled,
  currentCaption,
  source,
  groupId,
  labelIds = [],
  selectedContentLabels = [],
  platforms,
  formId,
  onUndo,
  onApply,
}: Props) {
  const normalizedCurrentCaption = currentCaption ?? "";
  const inputIdPrefix = useId();
  const instructionInputId = `${inputIdPrefix}-instruction`;
  const toneProfileInputId = `${inputIdPrefix}-tone-profile`;
  const translateLanguageInputId = `${inputIdPrefix}-translate-language`;
  const [open, setOpen] = useState(false);
  const [moreActionsOpen, setMoreActionsOpen] = useState(false);
  const [panel, setPanel] = useState<AiPanel>("generate");
  const [panelBeforeSettings, setPanelBeforeSettings] =
    useState<Exclude<AiPanel, "settings">>("generate");
  const [panelBeforeTranslate, setPanelBeforeTranslate] = useState<
    "refine" | null
  >(null);
  const trackEvent = useTrackEvent();
  const descriptions = useSelector((state: ReduxRootState) =>
    getDescriptions(state, formId ?? null)
  );
  const { brandKit } = useBrandKit(open ? groupId || null : null);
  const [instruction, setInstruction] = useState("");
  const [refineInstruction, setRefineInstruction] = useState("");
  const [selectedRefineInstructions, setSelectedRefineInstructions] = useState<
    string[]
  >([]);
  const [translateQuery, setTranslateQuery] = useState("");
  const [placeholderText, setPlaceholderText] = useState(() =>
    getRandomCaptionPlaceholder()
  );
  const [includeEmoji, setIncludeEmoji] = useState(true);
  const [includeSelectedLabels, setIncludeSelectedLabels] = useState(
    labelIds.length > 0 && selectedContentLabels.length > 0
  );
  const [toneOfVoiceProfileId, setToneOfVoiceProfileId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasGeneratedCaption, setHasGeneratedCaption] = useState(false);
  const [lastInstruction, setLastInstruction] = useState<string | undefined>();
  const pendingMenuPanelRef = useRef<AiPanel | null>(null);
  const toolbarSlotRef = useRef<HTMLDivElement>(null);
  const toolbarActionWidthsRef = useRef<number[]>([]);
  const overflowActionButtonRef = useRef<HTMLButtonElement>(null);
  const [visibleToolbarActionCount, setVisibleToolbarActionCount] = useState(3);

  const toneProfiles = useMemo(
    () =>
      (brandKit?.toneOfVoiceProfiles || []).filter(
        (profile: ToneProfile) =>
          profile.currentState === "success" && profile.toneOfVoiceResult
      ),
    [brandKit?.toneOfVoiceProfiles]
  );
  const selectedToneProfileId = toneProfiles.some(
    (profile: ToneProfile) => profile._id === toneOfVoiceProfileId
  )
    ? toneOfVoiceProfileId
    : "";

  const hasSelectedLabels =
    labelIds.length > 0 && selectedContentLabels.length > 0;

  const trimmedCurrentCaption = normalizedCurrentCaption.trim();
  const hasCaption =
    trimmedCurrentCaption !== "" &&
    !loadingCaptionTexts.has(trimmedCurrentCaption);
  const canGenerate = instruction.trim() !== "";
  const canRefine =
    refineInstruction.trim() !== "" || selectedRefineInstructions.length > 0;

  useLayoutEffect(() => {
    const toolbarSlot = toolbarSlotRef.current;
    if (!toolbarSlot || typeof ResizeObserver === "undefined") {
      return;
    }

    const updateVisibleActions = (availableWidth: number) => {
      const actionWidths = toolbarActionWidthsRef.current;
      const overflowButtonWidth =
        overflowActionButtonRef.current?.getBoundingClientRect().width || 0;

      if (actionWidths.length < 3 || !overflowButtonWidth) {
        return;
      }

      let occupiedWidth =
        overflowButtonWidth + INLINE_ACTION_TOOLBAR_BORDER_WIDTH;
      let nextVisibleActionCount = 0;

      for (const actionWidth of actionWidths) {
        if (occupiedWidth + actionWidth > availableWidth) {
          break;
        }

        occupiedWidth += actionWidth;
        nextVisibleActionCount += 1;
      }

      setVisibleToolbarActionCount(nextVisibleActionCount);
    };

    const resizeObserver = new ResizeObserver(([entry]) => {
      updateVisibleActions(entry?.contentRect.width || 0);
    });

    resizeObserver.observe(toolbarSlot);
    updateVisibleActions(toolbarSlot.getBoundingClientRect().width);

    return () => resizeObserver.disconnect();
  }, [hasCaption, hasGeneratedCaption]);

  useEffect(() => {
    const timeout =
      !isLoading && hasGeneratedCaption && hasCaption
        ? window.setTimeout(() => setHasGeneratedCaption(false), 0)
        : null;

    return () => {
      if (timeout !== null) {
        window.clearTimeout(timeout);
      }
    };
  }, [hasCaption, hasGeneratedCaption, isLoading]);

  const mediaDescriptions = descriptions
    .map((item) => item.description.trim())
    .filter(Boolean);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen) {
      setMoreActionsOpen(false);
      setInstruction("");
      setRefineInstruction("");
      setSelectedRefineInstructions([]);
      setTranslateQuery("");
      setPanel("generate");
      setPanelBeforeTranslate(null);
      return;
    }

    setPlaceholderText(getRandomCaptionPlaceholder());
    setInstruction("");
    setRefineInstruction("");
    setSelectedRefineInstructions([]);
    setTranslateQuery("");
    setIncludeEmoji(true);
    setIncludeSelectedLabels(hasSelectedLabels);
  };

  const openPanel = (nextPanel: AiPanel) => {
    setMoreActionsOpen(false);

    if (nextPanel === "refine") {
      trackEvent({
        eventName: INLINE_AI_EVENTS.refine,
        metadata: { source, state: "opened" },
      });
      setRefineInstruction("");
      setSelectedRefineInstructions([]);
    }

    if (nextPanel === "settings") {
      setPanelBeforeSettings(panel === "settings" ? "generate" : panel);
    }

    if (nextPanel === "translate") {
      setPanelBeforeTranslate(panel === "refine" ? "refine" : null);
    }

    setPanel(nextPanel);
    setOpen(true);
  };

  const generateCaption = async ({
    instruction: nextInstruction,
    loadingCaption,
    sourceCaption = normalizedCurrentCaption,
  }: {
    instruction?: string;
    loadingCaption: string;
    sourceCaption?: string | null;
  }) => {
    if (!groupId) {
      toast.error("Select an account group to generate an AI caption.");
      return;
    }

    const normalizedInstruction = nextInstruction?.trim() || undefined;
    const normalizedCaption = (sourceCaption ?? "").trim() || undefined;

    if (!normalizedInstruction && !normalizedCaption) {
      toast.error("Enter a caption prompt first.");
      return;
    }

    const previousCaption = normalizedCurrentCaption;
    const wasGeneratedCaption = hasGeneratedCaption;

    setIsLoading(true);
    setOpen(false);
    // Keep the temporary loading copy and its eventual replacement in one
    // Lexical history entry. Undo should return to the user's previous copy,
    // never to a loading placeholder.
    onApply(loadingCaption, { history: "push" });

    try {
      const suggestions = await generateCaptionSuggestions(groupId, {
        instruction: normalizedInstruction,
        caption: normalizedCaption,
        platforms,
        labelIds: includeSelectedLabels ? labelIds : undefined,
        toneOfVoiceProfileId: selectedToneProfileId || undefined,
        includeEmoji,
        mediaDescriptions,
        suggestionCount: REQUESTED_SUGGESTION_COUNT,
      });
      const nextCaption = getSuggestionCaption(suggestions);

      if (!nextCaption) {
        throw new Error("No caption was generated");
      }

      onApply(nextCaption, { history: "merge" });
      setHasGeneratedCaption(true);
      setLastInstruction(normalizedInstruction);
    } catch (error) {
      onApply(previousCaption, { history: "merge" });
      setHasGeneratedCaption(wasGeneratedCaption);
      toast.error(
        getErrorMessage(error) ||
          "Failed to generate an AI caption. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerate = () => {
    trackEvent({
      eventName: INLINE_AI_EVENTS.generate,
      metadata: {
        source,
        state: "generated",
        include_emojis: includeEmoji,
        include_selected_labels: includeSelectedLabels,
        tone_of_voice_active: Boolean(selectedToneProfileId),
      },
    });
    void generateCaption({
      instruction,
      loadingCaption: GENERATING_CAPTION_TEXT,
    });
  };

  const handleTryAgain = () => {
    trackEvent({
      eventName: INLINE_AI_EVENTS.tryAgain,
      metadata: {
        source,
        include_emojis: includeEmoji,
        include_selected_labels: includeSelectedLabels,
        tone_of_voice_active: Boolean(selectedToneProfileId),
      },
    });
    void generateCaption({
      instruction:
        lastInstruction ||
        "Write a new caption that is different from the current caption.",
      sourceCaption: currentCaption,
      loadingCaption: REGENERATING_CAPTION_TEXT,
    });
  };

  const handleRefine = () => {
    const selectedInstructions = selectedRefineInstructions.join(" ");

    trackEvent({
      eventName: INLINE_AI_EVENTS.refine,
      metadata: { source, state: "generated" },
    });
    void generateCaption({
      instruction: [selectedInstructions, refineInstruction.trim()]
        .filter(Boolean)
        .join(" "),
      sourceCaption: currentCaption,
      loadingCaption: REFINING_CAPTION_TEXT,
    });
  };

  const handleTranslate = (language: string) => {
    trackEvent({
      eventName: INLINE_AI_EVENTS.translated,
      metadata: { source, language },
    });
    void generateCaption({
      instruction: `Translate this caption to ${language}.`,
      sourceCaption: currentCaption,
      loadingCaption: TRANSLATING_CAPTION_TEXT,
    });
  };

  const handleStartFresh = () => {
    trackEvent({
      eventName: INLINE_AI_EVENTS.startFresh,
      metadata: { source },
    });
    setHasGeneratedCaption(false);
    setLastInstruction(undefined);
    handleOpenChange(false);
    onApply("");
  };

  const openPanelFromMenu = (nextPanel: AiPanel) => {
    pendingMenuPanelRef.current = nextPanel;
    setMoreActionsOpen(false);
  };

  const handleMenuCloseAutoFocus = (event: Event) => {
    const nextPanel = pendingMenuPanelRef.current;
    if (!nextPanel) {
      return;
    }

    event.preventDefault();
    pendingMenuPanelRef.current = null;
    openPanel(nextPanel);
  };

  const toggleRefineInstruction = (instructionToToggle: string) => {
    setSelectedRefineInstructions((instructions) =>
      instructions.includes(instructionToToggle)
        ? instructions.filter((item) => item !== instructionToToggle)
        : [...instructions, instructionToToggle]
    );
  };

  const handleToneProfileChange = (
    nextToneProfileId: string,
    context: "generate" | "refine"
  ) => {
    setToneOfVoiceProfileId(nextToneProfileId);
    trackEvent({
      eventName: INLINE_AI_EVENTS.toneChanged,
      metadata: { source, context },
    });
  };

  if (hasGeneratedCaption || hasCaption) {
    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <div ref={toolbarSlotRef} className="min-w-7 flex-1">
          <PopoverAnchor asChild>
            <div
              style={{
                background:
                  "linear-gradient(#fff, #fff) padding-box, linear-gradient(90deg, #FFE4E4 0%, #DDD8FF 100%) border-box",
              }}
              className="group/ai inline-flex h-6 max-w-full min-w-0 items-center overflow-hidden rounded-[8px] border-1 border-transparent shadow-[0_1px_1px_rgba(16,24,40,0.05)]"
            >
              {visibleToolbarActionCount >= 1 && (
                <ToolbarButton
                  label="Refine"
                  icon="fa-regular fa-sparkles"
                  onClick={() => openPanel("refine")}
                  className="w-auto pl-1"
                  buttonRef={(button) => {
                    if (button) {
                      toolbarActionWidthsRef.current[0] =
                        button.getBoundingClientRect().width;
                    }
                  }}
                  disabled={disabled || isLoading}
                />
              )}
              {visibleToolbarActionCount >= 2 && (
                <ToolbarButton
                  label="Try again"
                  icon="fa-regular fa-rotate-right"
                  onClick={handleTryAgain}
                  buttonRef={(button) => {
                    if (button) {
                      toolbarActionWidthsRef.current[1] =
                        button.getBoundingClientRect().width;
                    }
                  }}
                  disabled={disabled || isLoading}
                />
              )}
              {visibleToolbarActionCount >= 3 && (
                <ToolbarButton
                  label="Start fresh"
                  icon="fa-regular fa-sparkles"
                  onClick={handleStartFresh}
                  buttonRef={(button) => {
                    if (button) {
                      toolbarActionWidthsRef.current[2] =
                        button.getBoundingClientRect().width;
                    }
                  }}
                  disabled={disabled || isLoading}
                />
              )}
              <DropdownMenu
                modal={false}
                open={moreActionsOpen}
                onOpenChange={setMoreActionsOpen}
              >
                <DropdownMenuTrigger asChild>
                  <button
                    ref={overflowActionButtonRef}
                    type="button"
                    aria-label="More AI caption actions"
                    disabled={disabled || isLoading}
                    className="inline-flex h-full w-[22px] shrink-0 items-center justify-center border-0 bg-white p-0 text-[#333] hover:bg-[#f9f9f9] disabled:cursor-not-allowed disabled:text-neutral-300"
                  >
                    {visibleToolbarActionCount < 1 ? (
                      <i className="fa-regular fa-sparkles text-[12px] leading-none" />
                    ) : (
                      <i className="fa-regular fa-ellipsis text-[12px] leading-none" />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  side="top"
                  className="min-w-[120px]"
                  onCloseAutoFocus={handleMenuCloseAutoFocus}
                >
                  {visibleToolbarActionCount < 1 && (
                    <DropdownMenuItem
                      onSelect={() => openPanelFromMenu("refine")}
                    >
                      <i className="fa-regular fa-sparkles text-[13px]" />
                      Refine
                    </DropdownMenuItem>
                  )}
                  {visibleToolbarActionCount < 2 && (
                    <DropdownMenuItem onSelect={handleTryAgain}>
                      <i className="fa-regular fa-rotate-right text-[13px]" />
                      Try again
                    </DropdownMenuItem>
                  )}
                  {visibleToolbarActionCount < 3 && (
                    <DropdownMenuItem onSelect={handleStartFresh}>
                      <i className="fa-regular fa-sparkles text-[13px]" />
                      Start fresh
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    disabled={!hasCaption}
                    onSelect={() => openPanelFromMenu("translate")}
                  >
                    <i className="fa-regular fa-language text-[13px]" />
                    Translate
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={onUndo}>
                    <i className="fa-regular fa-rotate-left text-[13px]" />
                    Undo
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </PopoverAnchor>
        </div>
        {renderPanelContent(panel)}
      </Popover>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <TooltipWrapper tooltip="AI caption" placement="top">
        <div
          className="border-1 border-transparent rounded-[8px]"
          style={{
            background:
              "linear-gradient(#fff, #fff) padding-box, linear-gradient(90deg, #FFE4E4 0%, #DDD8FF 100%) border-box",
          }}
        >
          <PopoverTrigger asChild>
            <ActionButton
              disabled={disabled || isLoading}
              focused={open}
              onClick={() =>
                trackEvent({
                  eventName: INLINE_AI_EVENTS.generate,
                  metadata: { source, state: "opened" },
                })
              }
              aria-label="Write with AI"
              aria-expanded={open}
              className="!text-[#333] !border-none !size-[22px] !h-[22px] shadow-[0_1px_1px_rgba(16,24,40,0.05)] !w-auto min-w-[22px] max-w-[22px] pl-0 pr-0 transition-all duration-200 hover:max-w-[250px] aria-expanded:max-w-[250px] justify-start"
            >
              <span className="pl-2 pr-2 w-6 h-6 flex justify-center items-center">
                <i className="fa-regular fa-sparkles text-[12px] leading-none" />
              </span>
              <span className="min-w-0 overflow-hidden whitespace-nowrap text-[12px]">
                <span className="flex pr-2">Write with AI</span>
              </span>
            </ActionButton>
          </PopoverTrigger>
        </div>
      </TooltipWrapper>
      {renderPanelContent(panel)}
    </Popover>
  );

  function renderPanelContent(nextPanel: AiPanel) {
    const isCompactPanel =
      nextPanel === "translate" || nextPanel === "settings";
    const panelWidthClass = isCompactPanel
      ? "w-[calc(100vw-32px)] max-w-[400px]"
      : "w-[calc(100vw-32px)] max-w-[480px]";

    return (
      <PopoverContent
        align="start"
        side="top"
        sideOffset={8}
        className={`${panelWidthClass} border-none bg-transparent p-0 shadow-none`}
      >
        <div className="relative flex max-h-[500px] w-full flex-col gap-4 overflow-hidden rounded-[8px] bg-white p-4 shadow-[0_4px_8px_-2px_rgba(16,24,40,0.1),0_2px_4px_-2px_rgba(16,24,40,0.06)] ring-1 ring-inset ring-[#ffbdb4]">
          <PanelHeader
            panel={nextPanel}
            title={getPanelTitle(nextPanel)}
            onBack={
              nextPanel === "settings"
                ? () => setPanel(panelBeforeSettings)
                : nextPanel === "translate" && panelBeforeTranslate === "refine"
                  ? () => {
                      setTranslateQuery("");
                      setPanelBeforeTranslate(null);
                      setPanel("refine");
                    }
                  : undefined
            }
          />
          {nextPanel === "generate" && (
            <PromptPanel
              inputId={instructionInputId}
              label="Tell Captivate what to write about"
              value={instruction}
              placeholder={placeholderText}
              buttonLabel="Create caption"
              disabled={disabled || isLoading || !canGenerate}
              onChange={setInstruction}
              onSubmit={handleGenerate}
              footer={renderSettingsFooter()}
            />
          )}
          {nextPanel === "refine" && (
            <RefinePanel
              inputId={toneProfileInputId}
              value={refineInstruction}
              onChange={setRefineInstruction}
              toneProfiles={toneProfiles}
              selectedToneProfileId={selectedToneProfileId}
              onToneProfileChange={(nextToneProfileId) =>
                handleToneProfileChange(nextToneProfileId, "refine")
              }
              selectedInstructions={selectedRefineInstructions}
              onSelectPreset={toggleRefineInstruction}
              onOpenTranslate={() => openPanel("translate")}
              disabled={disabled || isLoading || !canRefine}
              onSubmit={handleRefine}
              footer={renderSettingsFooter()}
            />
          )}
          {nextPanel === "translate" && (
            <TranslatePanel
              disabled={disabled || isLoading}
              inputId={translateLanguageInputId}
              value={translateQuery}
              onChange={setTranslateQuery}
              onSubmit={() => {
                const language = ChatGPTSupportedLanguagesData.find(
                  ({ code }) => code === translateQuery
                );
                if (language) {
                  handleTranslate(language.name);
                }
              }}
            />
          )}
          {nextPanel === "settings" && renderSettingsPanel()}
        </div>
      </PopoverContent>
    );
  }

  function renderSettingsPanel() {
    return (
      <div
        className={`flex min-h-0 flex-1 flex-col gap-4 overflow-auto ${scrollClass}`}
      >
        <div className="flex flex-col gap-2">
          <label
            htmlFor={toneProfileInputId}
            className="text-[14px] font-semibold leading-5 text-[#333]"
          >
            Tone of voice profile
          </label>
          <ToneProfileSelect
            inputId={toneProfileInputId}
            value={selectedToneProfileId}
            toneProfiles={toneProfiles}
            onChange={(nextToneProfileId) =>
              handleToneProfileChange(
                nextToneProfileId,
                panelBeforeSettings === "refine" ? "refine" : "generate"
              )
            }
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="text-[14px] font-semibold leading-5 text-[#333]">
            Options
          </div>
          <div className="flex flex-col gap-3">
            <ToggleRow
              checked={includeEmoji}
              label="Include emojis"
              onClick={() => setIncludeEmoji((value) => !value)}
            />
            <ToggleRow
              checked={includeSelectedLabels}
              disabled={!hasSelectedLabels}
              label="Include selected Labels"
              showInfo
              onClick={() => setIncludeSelectedLabels((value) => !value)}
            />
          </div>
        </div>
        {hasSelectedLabels && includeSelectedLabels && (
          <div className="-mt-1 flex flex-wrap gap-1 pl-9">
            {selectedContentLabels.map((label) => (
              <div
                key={label.value}
                className="inline-flex h-5 max-w-[160px] items-center gap-1 rounded bg-[#f9f9f9] pr-1 text-xs font-medium leading-4 text-[#333]"
              >
                <span
                  className="flex size-5 flex-none items-center justify-center rounded-bl rounded-tl text-xs leading-none text-[#333]"
                  style={{ backgroundColor: getLabelPillColor(label) }}
                >
                  {getLabelPillIcon(label)}
                </span>
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                  {label.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderSettingsFooter() {
    const toneProfileName = toneProfiles.find(
      (profile: ToneProfile) => profile._id === selectedToneProfileId
    )?.name;

    return (
      <AiSettingsFooter
        toneProfileName={toneProfileName || "Default"}
        includeEmoji={includeEmoji}
        includeSelectedLabels={includeSelectedLabels}
        labelsAvailable={hasSelectedLabels}
        onOpenSettings={() => openPanel("settings")}
      />
    );
  }
}

function getPanelTitle(panel: AiPanel) {
  if (panel === "refine") {
    return "Refine caption";
  }
  if (panel === "translate") {
    return "Translate";
  }
  if (panel === "settings") {
    return "Settings";
  }
  return "AI Caption";
}

function PanelHeader({
  panel,
  title,
  onBack,
}: {
  panel: AiPanel;
  title: string;
  onBack?: () => void;
}) {
  const icon =
    panel === "refine"
      ? "fa-wand-magic-sparkles"
      : panel === "translate"
        ? "fa-language"
        : panel === "settings"
          ? "fa-gear"
          : "fa-sparkles";

  return (
    <div className="flex h-5 shrink-0 items-center pr-7">
      <div className="flex items-center gap-2 text-[16px] font-bold leading-5 text-[#333] font-[family-name:var(--font-gabarito)]">
        {onBack ? (
          <button
            type="button"
            aria-label="Back to AI caption"
            onClick={onBack}
            className="inline-flex size-5 items-center justify-center rounded border-0 bg-transparent p-0 text-[#808080] hover:bg-[#f9f9f9]"
          >
            <i className="fa-regular fa-arrow-left text-[16px] leading-none" />
          </button>
        ) : null}
        <i
          className={`fa-regular ${icon} inline-flex size-5 items-center justify-center text-[16px] leading-none text-[#4f31ff]`}
        />
        <span>{title}</span>
      </div>
      <PopoverClose asChild>
        <PanelCloseButton />
      </PopoverClose>
    </div>
  );
}

function PanelCloseButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      type="button"
      aria-label="Close AI caption popover"
      className="absolute right-4 top-4 inline-flex size-5 items-center justify-center rounded border-0 bg-transparent p-0 text-[#808080] hover:bg-[#f9f9f9]"
    >
      <i className="fa-regular fa-xmark text-[16px] leading-none" />
    </button>
  );
}

function ToneProfileSelect({
  inputId,
  value,
  toneProfiles,
  onChange,
  className = "",
}: {
  inputId: string;
  value: string;
  toneProfiles: ToneProfile[];
  onChange: (value: string) => void;
  className?: string;
}) {
  return (
    <Select
      value={value || DEFAULT_TONE_PROFILE_VALUE}
      onValueChange={(nextValue) =>
        onChange(nextValue === DEFAULT_TONE_PROFILE_VALUE ? "" : nextValue)
      }
    >
      <SelectTrigger
        id={inputId}
        aria-label="Tone of voice profile"
        className={`h-8 min-h-8 rounded-[8px] border-[#e6e6e6] bg-white px-3 py-0 text-[14px] font-medium leading-5 text-[#333] shadow-[0_1px_1px_rgba(16,24,40,0.05)] focus:border-[#725aff] focus:ring-2 focus:ring-[#4e34e6]/10 ${className}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem
          value={DEFAULT_TONE_PROFILE_VALUE}
          className="text-[14px] font-medium leading-5 text-[#333]"
        >
          Default
        </SelectItem>
        {toneProfiles.map((profile) => (
          <SelectItem
            key={profile._id}
            value={profile._id}
            className="text-[14px] font-medium leading-5 text-[#333]"
          >
            {profile.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function PromptPanel({
  inputId,
  label,
  value,
  placeholder,
  buttonLabel,
  disabled,
  onChange,
  onSubmit,
  footer,
}: {
  inputId: string;
  label: string;
  value: string;
  placeholder: string;
  buttonLabel: string;
  disabled: boolean;
  onChange: (value: string) => void;
  onSubmit: () => void;
  footer: ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <label htmlFor={inputId} className="sr-only">
        {label}
      </label>
      <textarea
        id={inputId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-[84px] min-h-[84px] w-full resize-none rounded-[8px] border border-[#e6e6e6] bg-white p-3 text-[14px] font-normal leading-5 text-[#333] shadow-[0_1px_1px_rgba(16,24,40,0.05)] placeholder:text-[#808080] focus:border-[#725aff] focus:outline-none focus:ring-2 focus:ring-[#4e34e6]/10"
      />
      <PanelFooter controls={footer}>
        <SubmitButton disabled={disabled} onClick={onSubmit}>
          {buttonLabel}
        </SubmitButton>
      </PanelFooter>
    </div>
  );
}

function RefinePanel({
  inputId,
  value,
  disabled,
  onChange,
  toneProfiles,
  selectedToneProfileId,
  onToneProfileChange,
  selectedInstructions,
  onSelectPreset,
  onOpenTranslate,
  onSubmit,
  footer,
}: {
  inputId: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  toneProfiles: ToneProfile[];
  selectedToneProfileId: string;
  onToneProfileChange: (value: string) => void;
  selectedInstructions: string[];
  onSelectPreset: (value: string) => void;
  onOpenTranslate: () => void;
  onSubmit: () => void;
  footer: ReactNode;
}) {
  return (
    <div
      className={`flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto ${scrollClass}`}
    >
      <div className="flex shrink-0 flex-col gap-2">
        <label
          htmlFor={inputId}
          className="text-[14px] font-semibold leading-5 text-[#333]"
        >
          Tone of voice profile
        </label>
        <ToneProfileSelect
          inputId={inputId}
          value={selectedToneProfileId}
          toneProfiles={toneProfiles}
          onChange={onToneProfileChange}
          className="w-[200px] max-w-full"
        />
      </div>
      <div className="flex shrink-0 flex-col gap-2 shadow-[inset_0_-1px_0_#e6e6e6]">
        <div className="text-[14px] font-semibold leading-5 text-[#454545]">
          Preset prompts
        </div>
        {[refineOptions.slice(0, 2), refineOptions.slice(2)].map(
          (optionRow, rowIndex) => (
            <div
              key={rowIndex}
              className="flex min-h-12 flex-wrap items-center gap-2 rounded-[8px] bg-[#fcfaff] p-2"
            >
              {optionRow.map((option) => (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => onSelectPreset(option.instruction)}
                  aria-pressed={selectedInstructions.includes(
                    option.instruction
                  )}
                  className={`inline-flex h-8 items-center gap-2 rounded-[8px] border px-3 text-[14px] font-medium leading-5 shadow-[0_1px_2px_rgba(16,24,40,0.05)] transition-colors ${
                    selectedInstructions.includes(option.instruction)
                      ? "border-[#ddd5ff] bg-[#f2efff] text-[#4f31ff]"
                      : "border-[#e6e6e6] bg-white text-[#333] hover:bg-[#f9f9f9]"
                  }`}
                >
                  {option.label}
                  {selectedInstructions.includes(option.instruction) && (
                    <i className="fa-regular fa-check text-[12px] leading-none" />
                  )}
                </button>
              ))}
            </div>
          )
        )}
        <div className="flex h-12 items-center rounded-[8px] bg-[#fcfaff] p-2">
          <button
            type="button"
            onClick={onOpenTranslate}
            className="inline-flex h-8 w-full items-center justify-between rounded-[8px] border border-[#e6e6e6] bg-white px-3 text-[14px] font-medium leading-5 text-[#333] shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:bg-[#f9f9f9]"
          >
            Translate to
            <i
              className="fa-regular fa-chevron-right text-[12px] leading-none text-[#808080]"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
      <textarea
        aria-label="Refine caption instruction"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Try: Enhance the excitement of a zoo event featuring the arrival of a baby elephant"
        className="h-[84px] min-h-[84px] w-full resize-none rounded-[8px] border border-[#e6e6e6] bg-white p-3 text-[14px] font-normal leading-5 text-[#333] shadow-[0_1px_1px_rgba(16,24,40,0.05)] placeholder:text-[#808080] focus:border-[#725aff] focus:outline-none focus:ring-2 focus:ring-[#4e34e6]/10"
      />
      <PanelFooter controls={footer}>
        <SubmitButton disabled={disabled} onClick={onSubmit}>
          Refine caption
        </SubmitButton>
      </PanelFooter>
    </div>
  );
}

function TranslatePanel({
  inputId,
  disabled,
  value,
  onChange,
  onSubmit,
}: {
  inputId: string;
  disabled: boolean;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
}) {
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const languages = useMemo(
    () => orderBy(ChatGPTSupportedLanguagesData, ({ name }) => name, "asc"),
    []
  );
  const selectedLanguage = languages.find(({ code }) => code === value);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label
          htmlFor={inputId}
          className="text-[14px] font-semibold leading-5 text-[#333]"
        >
          Translate to
        </label>
        <Popover open={languageMenuOpen} onOpenChange={setLanguageMenuOpen}>
          <PopoverTrigger asChild>
            <button
              id={inputId}
              type="button"
              role="combobox"
              aria-expanded={languageMenuOpen}
              className="flex h-8 w-full items-center gap-2 rounded-[8px] border border-[#e6e6e6] bg-white px-3 text-[14px] font-normal leading-5 text-[#333] shadow-[0_1px_1px_rgba(16,24,40,0.05)] focus:border-[#725aff] focus:outline-none focus:ring-2 focus:ring-[#4e34e6]/10"
            >
              {selectedLanguage ? (
                <span aria-hidden="true" className="shrink-0">
                  <CountryFlag
                    countryCode={getCountryCodeForLanguage(
                      selectedLanguage.code
                    )}
                    size="sm"
                  />
                </span>
              ) : null}
              <span
                className={`min-w-0 flex-1 truncate text-left ${
                  selectedLanguage ? "text-[#333]" : "text-[#808080]"
                }`}
              >
                {selectedLanguage?.name ?? "Choose language"}
              </span>
              <i
                className="fa-regular fa-chevron-down size-5 shrink-0 text-center text-[16px] leading-5 text-[#808080]"
                aria-hidden="true"
              />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side="bottom"
            sideOffset={4}
            disableAnimation
            className="z-[1101] h-[264px] w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-64px)] overflow-hidden rounded-[8px] border border-[#e6e6e6] bg-white p-1 shadow-[0_4px_8px_-2px_rgba(16,24,40,0.1),0_2px_4px_-2px_rgba(16,24,40,0.06)]"
          >
            <Command className="rounded-none border-none [&_[data-slot=command-input-wrapper]]:h-8">
              <CommandInput
                placeholder="Search"
                className="h-8 py-1.5 text-[14px] font-normal leading-5"
              />
              <CommandList className={`max-h-[224px] ${scrollClass}`}>
                <CommandEmpty>No languages found.</CommandEmpty>
                <CommandGroup className="p-0">
                  {languages.map((language) => (
                    <CommandItem
                      key={language.code}
                      value={language.name}
                      onSelect={() => {
                        onChange(language.code);
                        setLanguageMenuOpen(false);
                      }}
                      className="h-8 cursor-pointer gap-2 rounded-none px-3 py-1.5 text-[14px] font-medium leading-5 text-[#333]"
                    >
                      <span aria-hidden="true" className="shrink-0">
                        <CountryFlag
                          countryCode={getCountryCodeForLanguage(language.code)}
                          size="sm"
                        />
                      </span>
                      {language.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex justify-end">
        <SubmitButton disabled={disabled || !value} onClick={onSubmit}>
          Translate
        </SubmitButton>
      </div>
    </div>
  );
}

function PanelFooter({
  children,
  controls,
}: {
  children: ReactNode;
  controls: ReactNode;
}) {
  return (
    <div className="flex h-8 shrink-0 items-center justify-between gap-3">
      {controls}
      {children}
    </div>
  );
}

function SubmitButton({
  disabled,
  onClick,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-8 shrink-0 cursor-pointer items-center rounded-[8px] border border-[#4f31ff] bg-[#4f31ff] px-3 text-[14px] font-medium leading-5 text-white shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:bg-[#4f31ff]/90 disabled:cursor-not-allowed disabled:border-[#cfc8ff] disabled:bg-[#cfc8ff] disabled:opacity-100"
    >
      {children}
    </button>
  );
}

function ToolbarButton({
  label,
  icon,
  disabled,
  onClick,
  className,
  buttonRef,
}: {
  label: string;
  icon: string;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
  buttonRef?: Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-full min-w-6 shrink-0 items-center gap-1 overflow-hidden border-0 bg-white px-2 text-xs font-medium leading-4 text-[#333] hover:bg-[#f9f9f9] disabled:cursor-not-allowed disabled:text-neutral-300 ${className || ""}`}
    >
      <i className={`${icon} shrink-0 text-[12px] leading-none`} />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

function AiSettingsFooter({
  toneProfileName,
  includeEmoji,
  includeSelectedLabels,
  labelsAvailable,
  onOpenSettings,
}: {
  toneProfileName: string;
  includeEmoji: boolean;
  includeSelectedLabels: boolean;
  labelsAvailable: boolean;
  onOpenSettings: () => void;
}) {
  return (
    <div className="flex h-8 min-w-0 items-center gap-2 overflow-hidden text-[14px] font-normal leading-5 text-[#808080]">
      <IconFooterControl
        label="Caption settings"
        icon="fa-regular fa-gear"
        onClick={onOpenSettings}
      />
      <SettingsIndicator label="TOV" value={toneProfileName} />
      <div className="flex h-5 items-center border-l border-[#e6e6e6] pl-2">
        <SettingsIndicator label="Emojis" value={includeEmoji ? "On" : "Off"} />
      </div>
      <div className="flex h-5 items-center border-l border-[#e6e6e6] pl-2">
        <SettingsIndicator
          label="Labels"
          value={labelsAvailable && includeSelectedLabels ? "On" : "Off"}
        />
      </div>
    </div>
  );
}

function SettingsIndicator({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1 text-[14px] font-normal leading-5 text-[#808080]">
      <span>{label}</span>
      <span className="max-w-[92px] truncate font-medium">{value}</span>
    </span>
  );
}

function IconFooterControl({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="inline-flex size-8 items-center justify-center rounded-[8px] border border-[#e6e6e6] bg-white p-0 text-[#333] shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:bg-[#f9f9f9]"
    >
      <i className={`${icon} text-[16px] leading-none`} />
    </button>
  );
}

function ToggleRow({
  checked,
  label,
  disabled,
  showInfo,
  onClick,
}: {
  checked: boolean;
  label: string;
  disabled?: boolean;
  showInfo?: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex h-5 items-center gap-2">
      <button
        type="button"
        role="switch"
        disabled={disabled}
        aria-checked={checked}
        aria-label={label}
        onClick={onClick}
        className={`relative h-4 w-8 flex-none rounded-full border-0 p-0 transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4f31ff]/35 disabled:cursor-not-allowed disabled:opacity-40 ${
          checked ? "bg-[#4f31ff]" : "bg-[#edeaff]"
        }`}
      >
        <span
          className={`absolute left-0.5 top-0.5 block size-3 rounded-full bg-white shadow-[0_1px_2px_rgba(16,24,40,0.18)] transition-transform duration-150 ${
            checked ? "translate-x-4" : "translate-x-0"
          }`}
        />
      </button>
      <div
        className={`flex items-center gap-2 text-[14px] font-semibold leading-5 text-[#333] ${disabled ? "opacity-40" : ""}`}
      >
        <span>{label}</span>
        {showInfo && (
          <i
            className="fa-regular fa-info-circle inline-flex size-[15px] items-center justify-center text-[12px] leading-none text-[#808080]"
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}
